import { useEffect, useState } from "react";
import {
  metricsAffinity, metricsConversion, metricsPipeline, metricsPlays,
  type AffinityCell, type ConversionMetrics, type PipelineMetrics, type PlaysMetrics,
} from "../lib/api";

export default function Overview() {
  const [plays, setPlays]            = useState<PlaysMetrics | null>(null);
  const [conv,  setConv]             = useState<ConversionMetrics | null>(null);
  const [pipe,  setPipe]             = useState<PipelineMetrics | null>(null);
  const [aff,   setAff]              = useState<AffinityCell[] | null>(null);
  const [error, setError]            = useState<string | null>(null);
  const [range, setRange]            = useState<"7d" | "30d" | "90d">("30d");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([metricsPlays(range), metricsConversion(), metricsPipeline(), metricsAffinity()])
      .then(([p, c, pi, a]) => {
        if (cancelled) return;
        setPlays(p); setConv(c); setPipe(pi); setAff(a);
      })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [range]);

  if (error) return <div className="text-sm text-rainfall-bad">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Overview</h1>
        <select
          value={range}
          onChange={e => setRange(e.target.value as "7d" | "30d" | "90d")}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Plays today"  value={plays?.totals.d1}  />
        <Stat label="Plays 7d"     value={plays?.totals.d7}  />
        <Stat label="Plays 30d"    value={plays?.totals.d30} />
        <Stat label="Completion 7d" value={fmtPct(plays?.totals.completion7d)} />
        <Stat label="Free installs"  value={conv?.freeInstalls} />
        <Stat label="Paid installs"  value={conv?.paidInstalls} />
        <Stat label="Conversion"     value={fmtPct(conv?.conversionRate)} />
        <Stat
          label="Pipeline success 7d"
          value={fmtPct(pipe?.successRate7d)}
          tone={pipe?.successRate7d != null && pipe.successRate7d < 0.9 ? "bad" : "muted"}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={`Daily plays (${range})`}>
          <Sparkbars data={plays?.timeseries ?? []} />
        </Card>
        <Card title="Pipeline">
          {pipe ? (
            <div className="text-sm">
              <div className="flex gap-4 text-xs text-slate-400 mb-3">
                {Object.entries(pipe.totals).map(([k, v]) => (
                  <span key={k}>{k}: <span className="font-mono text-slate-200">{v}</span></span>
                ))}
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Recent failures</div>
              {pipe.recentFailures.length === 0 ? (
                <div className="text-xs text-slate-500">None.</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {pipe.recentFailures.map(f => (
                    <li key={f.job_id} className="border-b border-slate-900 pb-1">
                      <span className="font-mono">{f.asset_id.slice(0, 8)}…</span>
                      <span className="text-slate-500"> · {f.created_at} · </span>
                      <span className="text-rainfall-bad">{f.error?.slice(0, 80) ?? ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : <div className="text-xs text-slate-500">Loading…</div>}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={`By theme (${range})`}>
          <BreakdownTable
            rows={(plays?.byTheme ?? []).map(r => ({ key: r.theme, plays: r.plays, completion: r.completion }))}
            keyHeader="Theme"
          />
        </Card>
        <Card title={`By voice (${range})`}>
          <BreakdownTable
            rows={(plays?.byVoice ?? []).map(r => ({ key: r.voice, plays: r.plays, completion: r.completion }))}
            keyHeader="Voice"
          />
        </Card>
      </div>

      <Card title="Affinity heatmap (theme × voice · last 30d completion rate)">
        <AffinityGrid cells={aff ?? []} />
      </Card>

      <Card title="Conversion · recent">
        {conv && conv.recentConversions.length > 0 ? (
          <ul className="text-xs space-y-1 font-mono">
            {conv.recentConversions.map(c => (
              <li key={c.install_id} className="border-b border-slate-900 pb-1">
                <span>{c.install_id.slice(0, 16)}…</span>
                <span className="text-slate-500 ml-3">{c.converted_at}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-slate-500">No conversions yet.</div>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-slate-800 rounded-lg bg-slate-900/30">
      <header className="px-4 py-2 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">{title}</header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string | undefined; tone?: "muted" | "bad" }) {
  const toneCls = tone === "bad" ? "text-rainfall-bad" : "text-slate-100";
  return (
    <div className="border border-slate-800 rounded-lg bg-slate-900/30 p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${toneCls}`}>{value ?? "—"}</div>
    </div>
  );
}

function Sparkbars({ data }: { data: PlaysMetrics["timeseries"] }) {
  if (data.length === 0) return <div className="text-xs text-slate-500">No plays in range.</div>;
  const max = Math.max(...data.map(d => d.total), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map(d => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.total} plays (${d.completed} completed, ${d.paid} paid)`}>
          <div className="w-full bg-rainfall-accent/30 rounded-sm relative" style={{ height: `${(d.total / max) * 100}%`, minHeight: 2 }}>
            <div className="absolute inset-x-0 bottom-0 bg-rainfall-accent rounded-sm" style={{ height: `${(d.completed / Math.max(d.total, 1)) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BreakdownTable({ rows, keyHeader }: { rows: { key: string; plays: number; completion: number | null }[]; keyHeader: string }) {
  if (rows.length === 0) return <div className="text-xs text-slate-500">No data.</div>;
  return (
    <table className="min-w-full text-sm">
      <thead className="text-xs text-slate-500 uppercase tracking-wider">
        <tr>
          <th className="text-left py-1 font-normal">{keyHeader}</th>
          <th className="text-right py-1 font-normal">Plays</th>
          <th className="text-right py-1 font-normal">Compl.</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.key} className="border-t border-slate-900">
            <td className="py-1">{r.key}</td>
            <td className="py-1 text-right font-mono">{r.plays}</td>
            <td className="py-1 text-right font-mono">{fmtPct(r.completion)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AffinityGrid({ cells }: { cells: AffinityCell[] }) {
  if (cells.length === 0) return <div className="text-xs text-slate-500">No data yet. Need plays from at least a few sessions for this to fill in.</div>;
  const themes = Array.from(new Set(cells.map(c => c.theme))).sort();
  const voices = Array.from(new Set(cells.map(c => c.voice))).sort();
  const lookup = new Map(cells.map(c => [`${c.theme}|${c.voice}`, c]));
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left text-slate-500"></th>
            {voices.map(v => <th key={v} className="px-2 py-1 text-left text-slate-500 font-normal">{v}</th>)}
          </tr>
        </thead>
        <tbody>
          {themes.map(t => (
            <tr key={t}>
              <td className="px-2 py-1 text-slate-300">{t}</td>
              {voices.map(v => {
                const c = lookup.get(`${t}|${v}`);
                return (
                  <td key={v} className="px-2 py-1">
                    {c ? <HeatCell completion={c.completionRate} plays={c.plays} /> : <span className="text-slate-700">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeatCell({ completion, plays }: { completion: number | null; plays: number }) {
  // Color from rose (low completion) → emerald (high), via amber midpoint.
  const c = completion ?? 0;
  let bg = "rgba(148,163,184,0.15)"; // slate-400/15
  if (completion != null) {
    if (c < 0.4)      bg = "rgba(244,63,94,0.30)";   // rose
    else if (c < 0.7) bg = "rgba(250,204,21,0.28)";  // amber
    else              bg = "rgba(74,222,128,0.30)";  // emerald
  }
  return (
    <div className="rounded px-2 py-1 font-mono text-xs min-w-[68px] text-center" style={{ backgroundColor: bg }}>
      {fmtPct(completion)}
      <span className="text-slate-500"> · {plays}</span>
    </div>
  );
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${Math.round(v * 100)}%`;
}
